import React, { Component } from "react";
import { Row, Col } from 'react-bootstrap';
import './_non-academic.scss';
import gql from "graphql-tag";
import { graphql } from "react-apollo";
import * as compose from "lodash.flowright";
import { withRouter } from "react-router-dom";
import PreloaderTwo from "../../../preloader/PreloaderTwo";
import { SectionCard, EmptyState, SummaryStatBox, FeeTable } from './naComponents';
import { GRADIENTS } from './naTheme';

// ─── GraphQL Query (unchanged) ────────────────────────────────────────────────

const GET_FEE_DETAILS = gql`
  query($student_mobile: String) {
    getStudentFeeDetails(student_mobile: $student_mobile) {
      total_fees { fee_type total_amount pending_amount }
      paid_fees  { fee_type paid_amount  paid_date      }
      due_fees   { fee_type due_date     due_amount     }
    }
  }
`;

// ─── Column definitions ───────────────────────────────────────────────────────

const totalFeeColumns = [
  { key: 'fee_type',       label: 'Fee Type' },
  {
    key: 'total_amount', label: 'Total Amount', bold: true,
    render: row => `₹${(row.total_amount || 0).toLocaleString()}`,
  },
  {
    key: 'pending_amount', label: 'Pending Amount', bold: true,
    colorFn: row => row.pending_amount > 0 ? '#B91C1C' : '#047857',
    render:  row => `₹${(row.pending_amount || 0).toLocaleString()}`,
  },
];

const paidFeeColumns = [
  { key: 'fee_type',    label: 'Fee Type' },
  {
    key: 'paid_amount', label: 'Paid Amount', bold: true,
    colorFn: () => '#047857',
    render: row => `₹${(row.paid_amount || 0).toLocaleString()}`,
  },
  { key: 'paid_date', label: 'Paid Date' },
];

const dueFeeColumns = [
  { key: 'fee_type',   label: 'Fee Type' },
  {
    key: 'due_amount', label: 'Due Amount', bold: true,
    colorFn: () => '#B91C1C',
    render: row => `₹${(row.due_amount || 0).toLocaleString()}`,
  },
  { key: 'due_date', label: 'Due Date' },
];

// ─── Component ────────────────────────────────────────────────────────────────

class NAFeeDetails extends Component {

  render() {
    const feeDetails = this.props.feeDetails;

    if (feeDetails.loading) return <PreloaderTwo />;
    if (feeDetails.error !== undefined) {
      alert("Server Error. " + feeDetails.error.message);
      return null;
    }

    // ── Data extraction (unchanged logic) ──────────────────────────────────
    let totalfee  = [];
    let paidfee   = [];
    let pendingfee = [];

    if (feeDetails.getStudentFeeDetails.length > 0) {
      totalfee   = feeDetails.getStudentFeeDetails[0].total_fees;
      paidfee    = feeDetails.getStudentFeeDetails[0].paid_fees;
      pendingfee = feeDetails.getStudentFeeDetails[0].due_fees;
    }

    // ── Summary totals ─────────────────────────────────────────────────────
    const totalFeeSum   = totalfee  .reduce((acc, f) => acc + (f.total_amount   || 0), 0);
    const paidFeeSum    = paidfee   .reduce((acc, f) => acc + (f.paid_amount    || 0), 0);
    const pendingFeeSum = pendingfee.reduce((acc, f) => acc + (f.due_amount     || 0), 0);

    return (
      <div className="feeDetails" style={{ paddingTop: '8px' }}>

        {/* ── Summary Stat Boxes ──────────────────────────────────────── */}
        <Row className="mb-3">
          <Col sm={12} md={4}>
            <SummaryStatBox
              iconClass="fas fa-receipt"
              label="Total Fees"
              value={`₹${totalFeeSum.toLocaleString()}`}
              gradient="#5B5FEF, #8B5CF6"
            />
          </Col>
          <Col sm={12} md={4}>
            <SummaryStatBox
              iconClass="fas fa-check-circle"
              label="Paid Fees"
              value={`₹${paidFeeSum.toLocaleString()}`}
              gradient="#10B981, #34D399"
            />
          </Col>
          <Col sm={12} md={4}>
            <SummaryStatBox
              iconClass="fas fa-exclamation-circle"
              label="Pending Fees"
              value={`₹${pendingFeeSum.toLocaleString()}`}
              gradient="#EF4444, #F87171"
            />
          </Col>
        </Row>

        {/* ── Total Fee Breakdown ─────────────────────────────────────── */}
        <div className="mb-3">
          <SectionCard
            title="Total Fee Breakdown"
            iconClass="fas fa-list-ul"
            headerBg={GRADIENTS.purple}
            bodyPadding="0"
          >
            {totalfee.length > 0
              ? <FeeTable data={totalfee} columns={totalFeeColumns} />
              : <EmptyState message="No fee records found" iconClass="fas fa-receipt" />
            }
          </SectionCard>
        </div>

        {/* ── Payment History ─────────────────────────────────────────── */}
        <div className="mb-3">
          <SectionCard
            title="Payment History"
            iconClass="fas fa-check-double"
            headerBg={GRADIENTS.paid}
            bodyPadding="0"
          >
            {paidfee.length > 0
              ? <FeeTable data={paidfee} columns={paidFeeColumns} />
              : <EmptyState message="No payments recorded" iconClass="fas fa-check-circle" />
            }
          </SectionCard>
        </div>

        {/* ── Due Fees ────────────────────────────────────────────────── */}
        <div className="mb-3">
          <SectionCard
            title="Due Fees"
            iconClass="fas fa-exclamation-triangle"
            headerBg={GRADIENTS.due}
            bodyPadding="0"
          >
            {pendingfee.length > 0
              ? <FeeTable data={pendingfee} columns={dueFeeColumns} />
              : (
                <div style={{ padding: '20px' }}>
                  <EmptyState message="No pending fees — all clear!" iconClass="fas fa-smile" />
                </div>
              )
            }
          </SectionCard>
        </div>

      </div>
    );
  }
}

// ─── Apollo HOC (unchanged) ───────────────────────────────────────────────────

export default withRouter(
  compose(
    graphql(GET_FEE_DETAILS, {
      options: props => ({
        variables:   { student_mobile: props.stateData.studentData.contact_no.toString() },
        fetchPolicy: 'network-only',
      }),
      name: "feeDetails",
    }),
  )(NAFeeDetails)
);
